import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';

interface SuspendCartModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (customerName: string, notes: string) => Promise<void>;
  itemCount: number;
  total: number;
  formatPrice: (price: number) => string;
}

export const SuspendCartModal = ({
  open,
  onClose,
  onConfirm,
  itemCount,
  total,
  formatPrice,
}: SuspendCartModalProps) => {
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(customerName, notes);
      setCustomerName('');
      setNotes('');
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCustomerName('');
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PauseCircleOutlineIcon color="warning" />
        Mettre en attente
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {itemCount} article{itemCount > 1 ? 's' : ''} - {formatPrice(total)}
          </Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          label="Nom du client (optionnel)"
          placeholder="ex: Mohamed, Table 5..."
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 100 }}
        />

        <TextField
          fullWidth
          label="Notes (optionnel)"
          placeholder="ex: Attend un ami, revient dans 10min..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          inputProps={{ maxLength: 500 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleConfirm}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <PauseCircleOutlineIcon />}
        >
          {isSubmitting ? 'En cours...' : 'Mettre en attente'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
